"use client"

import React, { createContext, useContext, useState, useEffect } from "react"

interface SiteSettings {
  phoneNumber: string
  email: string
  theme: string
  address: string
  design: string
}

interface SettingsContextType {
  settings: SiteSettings
  updateSettings: (newSettings: Partial<SiteSettings>) => void
  isLoading: boolean
}

const defaultSettings: SiteSettings = {
  phoneNumber: "+1 (734) 999-6962",
  email: "Sebastian@worldconnectionsllc.com",
  theme: "gray",
  address: "Ann Arbor, Michigan",  design: "classic",}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings)
  const [isLoading, setIsLoading] = useState(true)

  // Load settings from localStorage first (synchronous), then from API
  useEffect(() => {
    // First, check localStorage for immediate load (prevents flash)
    const stored = localStorage.getItem("site-settings")
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setSettings({ ...defaultSettings, ...parsed })
      } catch (error) {
        console.error("Failed to parse stored settings:", error)
      }
    }

    // Then fetch from API to ensure sync with server
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings')
        if (response.ok) {
          const data = await response.json()
          setSettings({ ...defaultSettings, ...data })
          // Sync localStorage with server
          localStorage.setItem("site-settings", JSON.stringify(data))
        }
      } catch (error) {
        console.error("Failed to fetch settings:", error)
      } finally {
        setIsLoading(false)
      }
    }
    
    fetchSettings()
  }, [])

  // Apply theme to document
  useEffect(() => {
    const html = document.documentElement
    // Remove all theme classes
    html.classList.remove('dark', 'theme-gray', 'theme-dark-gray', 'theme-blue', 'theme-light-blue', 'theme-light-green', 'theme-warm', 'theme-navy', 'theme-purple')
    // Add the selected theme class
    if (settings.theme && settings.theme !== 'light') {
      html.classList.add(settings.theme === 'dark' ? 'dark' : `theme-${settings.theme}`)
    }
  }, [settings.theme])

  const updateSettings = async (newSettings: Partial<SiteSettings>) => {
    const updated = { ...settings, ...newSettings }
    // Update UI immediately
    setSettings(updated)
    
    // Save to localStorage as backup
    localStorage.setItem("site-settings", JSON.stringify(updated))
    
    // Save to server
    try {
      const response = await fetch('/api/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updated),
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to save settings to server:', response.status, errorData)
      }
    } catch (error) {
      console.error('Error saving settings to server:', error)
    }
  }

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, isLoading }}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider")
  }
  return context
}
